import { Fieldset, Group, NumberInput, Select, Stack, Switch, Text, Textarea, TextInput } from '@mantine/core';
import HelpPopover from '../../HelpPopover/HelpPopover';
import KeyValueMapper from '../../KeyValueMapper';
import { RATE_BOUNDS, TIMEOUT_BOUNDS } from '../connectorConfig';
import { CAPS, MAX_ROWS_BOUNDS, TEXT_CHARS_BOUNDS } from '../connectorToolConfig';
import { CapabilitySlots } from './CapabilityPanels';
import RulesPanel from './RulesPanel';
import { HELP } from './workspaceHelp';

const ROOT_KINDS = [
  { value: 'array', label: 'Rows' },
  { value: 'object', label: 'One row' },
  { value: 'dictionary', label: 'Dictionary' },
];

const Label = ({ children, help }) => (
  <Group gap={4} component="span" wrap="nowrap">
    {children}
    {help}
  </Group>
);

const counter = (text, cap) => `${text.length} / ${cap}`;

// onUse for a help example that sets one field, undoable.
const fieldUse = (ws, set, read) =>
  ws.readOnly
    ? undefined
    : (value) => {
        const before = read();
        set(value);
        return () => set(before);
      };

export const RowsStep = ({ ws }) => {
  const { form, setField, errors, readOnly } = ws;
  const addMapping = (row) => {
    const before = form.mapRows;
    setField('mapRows', [...before, row]);
    return () => setField('mapRows', before);
  };

  return (
    <Stack gap="sm">
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <TextInput
          label={
            <Label
              help={
                <HelpPopover
                  {...HELP.root}
                  onUse={fieldUse(
                    ws,
                    (v) => setField('root', v),
                    () => form.root
                  )}
                />
              }
            >
              Rows come from
            </Label>
          }
          size="xs"
          ff="monospace"
          placeholder="data.items"
          style={{ flex: 1 }}
          disabled={readOnly}
          value={form.root}
          onChange={(event) => setField('root', event.target.value)}
        />
        <Select
          label="Root kind"
          size="xs"
          w={140}
          data={ROOT_KINDS}
          allowDeselect={false}
          disabled={readOnly}
          value={form.rootKind}
          onChange={(value) => setField('rootKind', value || 'array')}
        />
      </Group>

      {ws.isTool ? (
        <Stack gap={4}>
          <Group gap={4}>
            <Text size="sm" fw={600}>
              Fields each row carries
            </Text>
            <HelpPopover {...HELP.mapping} triggerLabel="Examples ▾" onUse={readOnly ? undefined : addMapping} />
          </Group>
          <Fieldset variant="unstyled" disabled={readOnly}>
            <KeyValueMapper
              rows={form.mapRows}
              setRows={(rows) => setField('mapRows', rows)}
              keyLabel="Row field"
              valueLabel="Response path"
              keyPlaceholder="place"
              valuePlaceholder="venue.name"
              addLabel="Add field"
            />
          </Fieldset>
          {errors.map && (
            <Text size="xs" c="red">
              {errors.map}
            </Text>
          )}
        </Stack>
      ) : (
        <Stack gap={4}>
          <CapabilitySlots ws={ws} />
          {errors.map && (
            <Text size="xs" c="red">
              {errors.map}
            </Text>
          )}
        </Stack>
      )}

      <RulesPanel form={form} setField={setField} errors={errors} readOnly={readOnly} fired={ws.fired} />
    </Stack>
  );
};

export const AgentStep = ({ ws }) => {
  const { form, setToolField, readOnly } = ws;
  const tool = form.tool;
  const found = ws.errors.tool || {};
  const textHelp = (key) => (
    <HelpPopover
      {...HELP[key]}
      onUse={fieldUse(
        ws,
        (v) => setToolField(key, v),
        () => tool[key]
      )}
    />
  );

  return (
    <Stack gap="sm">
      <TextInput
        label="Name"
        description={`The model calls it store_${tool.name.trim() || '<name>'}`}
        size="xs"
        ff="monospace"
        leftSection={<Text size="xs">store_</Text>}
        leftSectionWidth={52}
        placeholder="check_availability"
        disabled={readOnly}
        error={found.name}
        value={tool.name}
        onChange={(event) => setToolField('name', event.target.value)}
      />
      <Textarea
        label={<Label help={textHelp('description')}>Description</Label>}
        description={counter(tool.description, CAPS.description)}
        size="xs"
        autosize
        minRows={2}
        disabled={readOnly}
        error={found.description}
        value={tool.description}
        onChange={(event) => setToolField('description', event.target.value)}
      />
      <Textarea
        label={<Label help={textHelp('guidance')}>Guidance</Label>}
        description={counter(tool.guidance, CAPS.guidance)}
        size="xs"
        autosize
        minRows={2}
        disabled={readOnly}
        error={found.guidance}
        value={tool.guidance}
        onChange={(event) => setToolField('guidance', event.target.value)}
      />
      <TextInput
        label={<Label help={textHelp('notFoundText')}>Not-found text</Label>}
        size="xs"
        disabled={readOnly}
        error={found.notFoundText}
        value={tool.notFoundText}
        onChange={(event) => setToolField('notFoundText', event.target.value)}
      />
      <TextInput
        label={<Label help={textHelp('statusText')}>Status while it runs</Label>}
        description={counter(tool.statusText, CAPS.statusText)}
        size="xs"
        maxLength={CAPS.statusText}
        placeholder="Checking the latest details"
        disabled={readOnly}
        error={found.statusText}
        value={tool.statusText}
        onChange={(event) => setToolField('statusText', event.target.value)}
      />
    </Stack>
  );
};

export const SafetyStep = ({ ws }) => {
  const { form, setField, setToolField, errors, readOnly } = ws;
  const found = errors.tool || {};

  return (
    <Stack gap="sm">
      {ws.isTool && (
        <Switch
          label={<Label help={<HelpPopover {...HELP.personal} />}>Personal</Label>}
          description="Answers about the signed-in shopper only."
          disabled={readOnly}
          error={found.personal}
          checked={form.tool.personal}
          onChange={(event) => setToolField('personal', event.currentTarget.checked)}
        />
      )}
      <Group gap="xs" align="flex-start">
        {ws.isTool && (
          <NumberInput
            label="Max rows"
            size="xs"
            w={120}
            min={MAX_ROWS_BOUNDS.min}
            max={MAX_ROWS_BOUNDS.max}
            disabled={readOnly}
            error={found.maxRows}
            value={form.tool.maxRows}
            onChange={(value) => setToolField('maxRows', value)}
          />
        )}
        {ws.isTool && (
          <NumberInput
            label={
              <Label
                help={
                  <HelpPopover
                    {...HELP.maxTextChars}
                    onUse={fieldUse(
                      ws,
                      (v) => setToolField('maxTextChars', v),
                      () => form.tool.maxTextChars
                    )}
                  />
                }
              >
                Text per field
              </Label>
            }
            size="xs"
            w={120}
            min={TEXT_CHARS_BOUNDS.min}
            max={TEXT_CHARS_BOUNDS.max}
            step={100}
            allowDecimal={false}
            placeholder="300"
            disabled={readOnly}
            error={found.maxTextChars}
            value={form.tool.maxTextChars}
            onChange={(value) => setToolField('maxTextChars', value)}
          />
        )}
        <NumberInput
          label="Timeout (ms)"
          size="xs"
          w={120}
          min={TIMEOUT_BOUNDS.min}
          max={TIMEOUT_BOUNDS.max}
          disabled={readOnly}
          error={errors.timeoutMs}
          value={form.timeoutMs}
          onChange={(value) => setField('timeoutMs', value)}
        />
        <NumberInput
          label="Calls a minute"
          size="xs"
          w={120}
          min={RATE_BOUNDS.min}
          max={RATE_BOUNDS.max}
          disabled={readOnly}
          error={errors.rateLimitPerMinute}
          value={form.rateLimitPerMinute}
          onChange={(value) => setField('rateLimitPerMinute', value)}
        />
      </Group>
    </Stack>
  );
};
